import { createAction } from '@reduxjs/toolkit'
import { all, call, put, select, takeEvery } from 'redux-saga/effects'

import { draw2DMarkup } from './markupDocument'
import { deleteMaterialModification, deleteOpenings, fetchMaterialModifications } from '../../../api/projects-api'
import { fetchOpeningGroupsByProjectId } from '../../../api/takeoff-api'
import { OpeningGroupAPI } from '../../../models/activeDrawable'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Select } from '../../lib/toolBoxes/2D'
import { selectLastOverlayInsertTime } from '../../slices/2D'
import {
    removeSavedMaterialModification,
    resetMaterialModificationConflicts,
    updateConflictResolutionFlag,
    updateOverlaidChunkLockedFlag,
    updateReviewingConflictsFlag,
} from '../../slices/masterSetPlan'
import { selectProjectId } from '../../slices/project'

interface cancelOverlayActionPayload {
    overlayChunkId: number
    isConflictResolutionComplete: boolean
    isOverlaidChunkLocked: boolean
}

export const cancelOverlayAction = createAction<cancelOverlayActionPayload>('cancelOverlay')

export function* handleDeleteCancelledOptionEstimation(action: ReturnType<typeof cancelOverlayAction>) {
    try {
        const manager: PaperManager | null = yield call(managers.get2DManager)

        if (!manager) return

        const selectTool: Select = yield call(manager.getTool, Select.NAME)

        yield call(selectTool.exitSelectMode.bind(selectTool))

        const { overlayChunkId, isConflictResolutionComplete, isOverlaidChunkLocked } = action.payload

        if (isConflictResolutionComplete) yield put(updateConflictResolutionFlag(false))
        if (isOverlaidChunkLocked) yield put(updateOverlaidChunkLockedFlag(false))

        yield put(updateReviewingConflictsFlag(false))

        yield put(resetMaterialModificationConflicts())
        yield put(draw2DMarkup())

        const projectId: number | null = yield select(selectProjectId)

        if (!projectId) return

        const lastOverlayInsertTime = yield select(selectLastOverlayInsertTime)

        if (!lastOverlayInsertTime) return

        // Fetch all material modifications from api
        const allMaterialModifications = yield call(fetchMaterialModifications, projectId)

        if (allMaterialModifications.length) {
            // filter the list to exclude ones that don't belong to the overlay and were created before lastOverlayInsertTime
            const materialModificationIdsToDelete: number[] = allMaterialModifications.reduce((matModIds, matMod) => {
                // concatenating the 'z' to the date string is required to correctly parse it as UTC
                const parsedDate = Date.parse(matMod.created_at + 'z')

                if (
                    parseInt(matMod.option_ids.split('_')[0], 10) === overlayChunkId &&
                    parsedDate > lastOverlayInsertTime &&
                    !matModIds.includes(matMod.id)
                ) {
                    matModIds.push(matMod.id)
                }

                return matModIds
            }, [] as number[])

            // delete the material modifications that were created since we last inserted an overlay
            if (materialModificationIdsToDelete.length) {
                yield all(materialModificationIdsToDelete.map((id) => call(deleteMaterialModification, id, projectId)))

                yield put(removeSavedMaterialModification(materialModificationIdsToDelete))
            }
        }

        // Fetch opening groups from api because newly created ones aren't in store yet
        const openingGroups: OpeningGroupAPI[] = yield call(fetchOpeningGroupsByProjectId, projectId)

        // flatten to just opening locations because that's what has the timestamp
        const allOpeningLocations = openingGroups
            .flatMap((group) => group.openings)
            .flatMap((op) => op.opening_locations)

        // filter the list to exclude ones that don't belong to the overlay and were created before lastOverlayInsertTime
        const openingIdsToDelete: number[] = allOpeningLocations.reduce((opLocIds, opLoc) => {
            // concatenating the 'z' to the date string is required to correctly parse it as UTC
            const parsedDate = Date.parse(opLoc.created_at + 'z')

            if (
                opLoc.document_chunk_id === overlayChunkId &&
                parsedDate > lastOverlayInsertTime &&
                !opLocIds.includes(opLoc.opening_id)
            ) {
                opLocIds.push(opLoc.opening_id)
            }

            return opLocIds
        }, [] as number[])

        // delete the openings that were created since we last inserted an overlay
        if (openingIdsToDelete.length) yield call(deleteOpenings, projectId, openingIdsToDelete)
    } catch (e) {
        console.error(e)
    }
}

export function* watchForOverlayCancel() {
    yield takeEvery(cancelOverlayAction.type, handleDeleteCancelledOptionEstimation)
}
